/**
 * The Norican corporate mark.
 *
 * The design system does not ship one. `@norican/icons` exports only `Icon` and
 * `Flag`, and `@norican/react` carries no brand component, so this is a marked
 * workaround per the repo's rule: use the design system, and where it cannot
 * express something, raise a gap (`/raise-ds-gap`) and ship a workaround rather
 * than reaching for another library. The gap here is "no brand mark component".
 *
 * The artwork is the same file NOVA uses, not a re-trace: two assets rather than
 * one recoloured file, because the mark is navy (#03193d) on light and white on
 * dark. Tinting a single asset with `currentColor` would be less code but would
 * repaint the mark in whatever the surrounding text colour happens to be, and a
 * brand mark is not text.
 *
 * The app has light and dark, so the asset follows the active theme: navy on
 * light, white on dark. This is the one place in the app that cannot be solved
 * with a token, because the mark is artwork rather than text — recolouring a
 * single file with `currentColor` would repaint the brand in whatever the
 * surrounding text colour happens to be.
 *
 * `width` is derived from the artwork's own 277.88 x 94.82 viewBox, so a caller
 * passing an arbitrary box cannot stretch the mark.
 */

import { useThemeChoice } from "@/lib/theme";

/** Intrinsic aspect ratio of the artwork (277.88 / 94.82). */
const ASPECT_RATIO = 2.9306;

interface NoricanLogoProps {
  /** Rendered height in pixels; width follows the artwork's aspect ratio. */
  height?: number;
  className?: string;
}

export function NoricanLogo({ height = 20, className }: NoricanLogoProps) {
  const { theme } = useThemeChoice();
  return (
    <img
      src={theme === "dark" ? "/brand/norican-logo-white.svg" : "/brand/norican-logo-dark.svg"}
      // Decorative: the mark always sits beside the product name, so a screen
      // reader that announced both would read the company twice.
      alt=""
      aria-hidden="true"
      height={height}
      width={Math.round(height * ASPECT_RATIO)}
      className={className}
    />
  );
}
